import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getProductOverview } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker,
  NdoeStatusBadge, NdoeImage, NdoeStats, NdoeStatCard,
  NdoeError, NdoeLoading, useFicheArticle,
} from '../../components/ndoe';
import { money, days, pct, dateTxt } from './format';
import './rapports.css';

// Les six rapports du module, dans l'ordre où on les consulte.
const RAPPORTS = [
  { route: '/rapports/decisions', titre: 'Décisions recommandées', desc: 'Ce qu\'il faut faire cette semaine.', icone: '🎯' },
  { route: '/rapports/performance', titre: 'Performance produits', desc: 'Ce qui se vend, vite, et rapporte.', icone: '📊' },
  { route: '/rapports/ruptures', titre: 'Ruptures intéressantes', desc: 'Se vendait bien, vient de tomber à zéro.', icone: '🔴' },
  { route: '/rapports/rotation', titre: 'Rotation stock', desc: 'Ce qui tourne, et quand réassortir.', icone: '🔄' },
  { route: '/rapports/dormants', titre: 'Produits dormants', desc: 'Le capital qui dort en rayon.', icone: '💤' },
  { route: '/rapports/pointures', titre: 'Analyse pointures', desc: 'Quelles pointures renforcer ou réduire.', icone: '📐' },
];

export default function VueEnsemble() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();
  // Le nom d'une paire mène à sa fiche — déjà le cas sur 6 rapports, pas ici.
  const { openFiche, ficheElement } = useFicheArticle();

  const [period, setPeriod] = useState({ period: 30 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await getProductOverview(apiParams);
      setData(d);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || "Impossible de charger la vue d'ensemble.");
    } finally { setLoading(false); }
  }, [apiParams]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const kpis = data?.kpis || {};
  const decisions = data?.decisions || {};
  const alertes = data?.alertes || {};
  const meta = data?.meta || {};

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  const ALERTES = [
    { cle: 'a_reassortir', label: 'À réassortir', route: '/rapports/ruptures', tone: 'ok' },
    { cle: 'pointures_a_renforcer', label: 'Pointures à renforcer', route: '/rapports/pointures', tone: 'ok' },
    { cle: 'a_remettre_en_avant', label: 'À remettre en avant', route: '/rapports/dormants', tone: 'info' },
    { cle: 'a_solder', label: 'À solder', route: '/rapports/dormants', tone: 'warn' },
    { cle: 'a_tester', label: 'À tester', route: '/rapports/dormants', tone: 'info' },
    { cle: 'ne_pas_recommander', label: 'Ne pas recommander', route: '/rapports/ruptures', tone: 'danger' },
  ];

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Vue d'ensemble"
        subtitle="L'état de la boutique en un coup d'œil, et les décisions qui en découlent."
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>
        )}
      </div>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : loading ? (
        <NdoeLoading rows={5} />
      ) : (
        <>
          {(decisions.top_semaine || []).length > 0 && (
            <section className="dc-semaine ve-semaine" role="button" tabIndex={0}
              onClick={() => navigate('/rapports/decisions')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/rapports/decisions'); }}>
              <h3>Que faire cette semaine</h3>
              <ol>
                {decisions.top_semaine.map((a) => (
                  <li key={a.id}>
                    <span className="dc-semaine__prio num">{a.priorite}</span>
                    <div>
                      <b>{a.type_label} — {a.nom}</b>
                      <small>{a.raison}</small>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="ve-semaine__cta">Voir les {decisions.actions_prioritaires} actions prioritaires →</p>
            </section>
          )}

          <NdoeStats className="pp-stats">
            <KpiCard label="Actions prioritaires" value={decisions.actions_prioritaires ?? 0} gold
              onClick={() => navigate('/rapports/decisions')} />
            <KpiCard label="Produits actifs" value={kpis.produits_actifs ?? 0}
              onClick={() => navigate('/rapports/performance')} />
            <KpiCard label="Paires vendues" value={kpis.paires_vendues ?? 0} />
            <KpiCard label="Chiffre d'affaires" value={money(kpis.chiffre_affaires)} />
            <KpiCard
              label={kpis.marge_couverture_pct ? `Marge (sur ${kpis.marge_couverture_pct} % du CA)` : 'Marge estimée'}
              value={kpis.marge_couverture_pct ? money(kpis.marge_estimee) : '—'} />
            <KpiCard label="Écoulement moyen" value={pct(kpis.taux_ecoulement_moyen)} />
            <KpiCard label="Délai FIFO moyen" value={days(kpis.delai_fifo_moyen)} />
          </NdoeStats>

          {kpis.donnees_estimees > 0 && (
            <p className="pp-note">
              <NdoeStatusBadge kind="custom" tone="warn" label="Date entrée estimée" />
              {' '}{kpis.donnees_estimees} produit(s) n'ont pas de réception tracée&nbsp;: leur délai FIFO
              n'est pas calculé. Les autres indicateurs restent fiables.
            </p>
          )}

          <h3 className="ve-titre">Ce qui demande une attention</h3>
          <div className="ve-alertes">
            {ALERTES.map((a) => (
              <button key={a.cle} type="button" className="ve-alerte" onClick={() => navigate(a.route)}>
                <span className="ve-alerte__n num">{alertes[a.cle] ?? 0}</span>
                <span className="ve-alerte__l">{a.label}</span>
                <NdoeStatusBadge kind="custom" tone={a.tone} dot label="" />
              </button>
            ))}
          </div>

          {(data?.top_produits || []).length > 0 && (
            <>
              <h3 className="ve-titre">Vos meilleures ventes</h3>
              <div className="ve-top">
                {/* La carte ouvre la FICHE de la paire, et non plus le rapport
                    performance. On clique le nom d'un modèle pour savoir ce
                    qu'il vaut — stock, coût, marge, tendance — pas pour
                    atterrir sur un tableau où il faut le retrouver. Rien n'est
                    perdu : la fiche mène elle-même au rapport performance et au
                    catalogue.
                    La carte est déjà un bouton : y imbriquer un NdoeArticleLink
                    serait du HTML invalide (bouton dans bouton) — c'est donc
                    toute la carte qui devient le lien. */}
                {data.top_produits.map((p) => (
                  <button key={p.id_produit} type="button" className="ve-top__item"
                    title={`Fiche de ${p.nom_produit}`}
                    onClick={() => openFiche(p.id_produit)}>
                    <NdoeImage src={p.img} alt={p.nom_produit} size="md" preview={false} />
                    <div className="ve-top__txt">
                      <b>{p.nom_produit}</b>
                      <small>{p.marque || '—'}</small>
                    </div>
                    <div className="ve-top__chiffres">
                      <b className="num">{p.quantite_vendue}</b>
                      <small className="num">{money(p.chiffre_affaires)}</small>
                    </div>
                    <NdoeStatusBadge kind="custom" tone={p.palier_tone} label={p.palier_label} />
                  </button>
                ))}
              </div>
            </>
          )}

          <h3 className="ve-titre">Les rapports</h3>
          <div className="ve-cartes">
            {RAPPORTS.map((r) => (
              <button key={r.route} type="button" className="ve-carte" onClick={() => navigate(r.route)}>
                <span className="ve-carte__icone" aria-hidden="true">{r.icone}</span>
                <b>{r.titre}</b>
                <small>{r.desc}</small>
              </button>
            ))}
          </div>
        </>
      )}
      {/* Le tiroir de la fiche paire, monte une fois pour la page. */}
      {ficheElement}
    </NdoePage>
  );
}
